export const FILE_PREVIEW_TELEMETRY_PATH = '/api/protected/telemetry/file-preview';

export const PREVIEW_STATES = [
  'no_media',
  'mbox_empty',
  'container',
  'file_info_failed',
  'mobile_unsupported',
  'video_error',
] as const;
export type PreviewState = (typeof PREVIEW_STATES)[number];

export const IDENTITY_KINDS = ['file_uuid', 's3_key_sha256', 'archive_member_sha256', 'none'] as const;
export type IdentityKind = (typeof IDENTITY_KINDS)[number];

export const BROWSER_ERROR_KINDS = ['error', 'unhandledrejection'] as const;
export type BrowserErrorKind = (typeof BROWSER_ERROR_KINDS)[number];

export const BROWSER_ERROR_NAMES = [
  'Error',
  'TypeError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'URIError',
  'EvalError',
  'AggregateError',
  'ChunkLoadError',
  'AbortError',
  'NotAllowedError',
  'NotSupportedError',
  'NotFoundError',
  'InvalidStateError',
  'QuotaExceededError',
  'SecurityError',
  'NetworkError',
  'TimeoutError',
  'DataCloneError',
  'NonErrorRejection',
  'OtherError',
] as const;
export type BrowserErrorName = (typeof BROWSER_ERROR_NAMES)[number];

export const KNOWN_EXTENSIONS: ReadonlySet<string> = new Set([
  '3g2', '3gp', '7z', 'aac', 'ai', 'aif', 'aiff', 'amr', 'apk', 'asf', 'avi', 'bin', 'bmp', 'bz2',
  'cab', 'cda', 'csv', 'dat', 'db', 'dcm', 'dmg', 'doc', 'docm', 'docx', 'dot', 'dotx', 'eml',
  'emlx', 'eps', 'epub', 'exe', 'flac', 'flv', 'gif', 'gz', 'heic', 'heif', 'htm', 'html', 'ico',
  'ics', 'img', 'iso', 'jfif', 'jpe', 'jpeg', 'jpg', 'json', 'jsonl', 'key', 'kml', 'kmz', 'log',
  'm4a', 'm4v', 'mbox', 'mbx', 'md', 'mdb', 'mht', 'mhtml', 'mid', 'midi', 'mkv', 'mov', 'mp2',
  'mp3', 'mp4', 'mpeg', 'mpg', 'msg', 'numbers', 'oga', 'ogg', 'ogv', 'ole', 'one', 'opus', 'ost',
  'otf', 'pages', 'pcap', 'pcapng', 'pdf', 'png', 'pps', 'ppsx', 'ppt', 'pptm', 'pptx', 'ps',
  'psd', 'pst', 'rar', 'raw', 'rtf', 'sqlite', 'sqlitedb', 'svg', 'tar', 'tgz', 'tif', 'tiff',
  'ts', 'ttf', 'txt', 'vcf', 'vob', 'wav', 'weba', 'webm', 'webp', 'wma', 'wmv', 'woff',
  'woff2', 'xls', 'xlsb', 'xlsm', 'xlsx', 'xml', 'xz', 'yaml', 'yml', 'zip',
]);

export const KNOWN_MIME_TYPES: ReadonlySet<string> = new Set([
  'application/gzip',
  'application/json',
  'application/msword',
  'application/octet-stream',
  'application/pdf',
  'application/rtf',
  'application/vnd.ms-excel',
  'application/vnd.ms-outlook',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.rar',
  'application/x-7z-compressed',
  'application/x-msdownload',
  'application/x-rar-compressed',
  'application/x-sqlite3',
  'application/x-tar',
  'application/xml',
  'application/zip',
  'audio/aac',
  'audio/amr',
  'audio/flac',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/webm',
  'audio/x-m4a',
  'audio/x-wav',
  'image/bmp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'image/webp',
  'message/rfc822',
  'text/calendar',
  'text/csv',
  'text/html',
  'text/markdown',
  'text/plain',
  'text/x-vcard',
  'text/xml',
  'video/3gpp',
  'video/mp4',
  'video/mpeg',
  'video/ogg',
  'video/quicktime',
  'video/webm',
  'video/x-flv',
  'video/x-matroska',
  'video/x-ms-wmv',
  'video/x-msvideo',
]);

export const OTHER = 'other';

export function allowlistedExtension(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().replace(/^\./, '').toLowerCase();
  if (!value) return null;
  return KNOWN_EXTENSIONS.has(value) ? value : OTHER;
}

export function allowlistedMime(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const value = raw.split(';')[0].trim().toLowerCase();
  if (!value) return null;
  return KNOWN_MIME_TYPES.has(value) ? value : OTHER;
}

export function allowlistedErrorName(raw: unknown): BrowserErrorName {
  if (raw === 'NonErrorRejection') return 'NonErrorRejection';
  return (BROWSER_ERROR_NAMES as readonly string[]).includes(raw as string)
    ? (raw as BrowserErrorName)
    : 'OtherError';
}

export type PreviewEventBody = {
  event: 'file_preview_unsupported';
  state: PreviewState;
  identity_kind: IdentityKind;
  mime?: string | null;
  extension?: string | null;
  case_id?: string | null;
  file_uuid?: string | null;
  key?: string | null;
};

export type BrowserErrorBody = {
  event: 'browser_uncaught_error';
  kind: BrowserErrorKind;
  error_name: string;
};

export type FilePreviewTelemetryBody = PreviewEventBody | BrowserErrorBody;
